import { create } from 'zustand';
import {
	DEFAULT_THEME_BY_SCHEME,
	isLightTheme,
	normalizeThemeMode,
	type ResolvedTheme,
	THEME_CLASSES,
	THEME_COLORS,
	type ThemeMode,
} from '../config';

export type { ResolvedTheme, ThemeMode } from '../config';

export const THEME_STORAGE_KEY = 'theme';

// NOTE: theme commits flash a fullscreen loader overlay (new theme colors)
// instead of a view transition. Hold length covers the loader intro.
export const THEME_SWAP_HOLD_MS = 1600;

let swapTimer: ReturnType<typeof setTimeout> | undefined;

export function resolveThemeMode(mode: ThemeMode): ResolvedTheme {
	if (mode !== 'auto') return mode;
	const prefersDark =
		typeof window !== 'undefined' &&
		window.matchMedia('(prefers-color-scheme: dark)').matches;
	return prefersDark
		? DEFAULT_THEME_BY_SCHEME.dark
		: DEFAULT_THEME_BY_SCHEME.light;
}

export function applyTheme(mode: ThemeMode) {
	if (typeof document === 'undefined') return;

	const resolved = resolveThemeMode(mode);
	const root = document.documentElement;

	root.classList.remove('light', 'dark', ...THEME_CLASSES);
	const isLight = isLightTheme(resolved);
	root.classList.add(isLight ? 'light' : 'dark', resolved);
	root.style.colorScheme = isLight ? 'light' : 'dark';
	if (mode === 'auto') {
		root.removeAttribute('data-theme');
	} else {
		root.setAttribute('data-theme', mode);
	}

	const themeColor = document.querySelector<HTMLMetaElement>(
		'meta[name="theme-color"]',
	);
	if (themeColor) themeColor.content = THEME_COLORS[resolved];
}

export function getStoredThemeMode(): ThemeMode {
	if (typeof window === 'undefined') return 'auto';
	try {
		const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
		return normalizeThemeMode(stored) ?? 'auto';
	} catch {
		// INFO: localStorage unavailable (private mode) — fall back to auto.
	}
	return 'auto';
}

type ThemeStore = {
	mode: ThemeMode;
	resolved: ResolvedTheme;
	/** True while the theme-swap loader overlay is on screen. */
	swapping: boolean;
	/** Increments on every commit so the overlay loader replays from the start. */
	swapId: number;
	preview: (mode: ThemeMode) => void;
	setMode: (mode: ThemeMode, onCommitted?: () => void) => void;
};

export const useThemeStore = create<ThemeStore>((set) => {
	const mode = getStoredThemeMode();
	const resolved = resolveThemeMode(mode);
	return {
		mode,
		resolved,
		swapping: false,
		swapId: 0,
		preview: (next) => {
			// NOTE: Preview changes stay immediate so the picker’s own carousel animation remains visible.
			applyTheme(next);
			set({ resolved: resolveThemeMode(next) });
		},
		setMode: (next, onCommitted) => {
			if (typeof window !== 'undefined') {
				try {
					window.localStorage.setItem(THEME_STORAGE_KEY, next);
				} catch {
					// INFO: localStorage unavailable (private mode) — theme still applies.
				}
			}
			// NOTE: apply first so the overlay loader paints in the new theme color.
			applyTheme(next);
			const prefersReducedMotion =
				typeof window !== 'undefined' &&
				window.matchMedia('(prefers-reduced-motion: reduce)').matches;
			set((state) => ({
				mode: next,
				resolved: resolveThemeMode(next),
				swapping: !prefersReducedMotion,
				swapId: state.swapId + 1,
			}));
			onCommitted?.();
			if (prefersReducedMotion) return;
			clearTimeout(swapTimer);
			swapTimer = setTimeout(() => {
				set({ swapping: false });
			}, THEME_SWAP_HOLD_MS);
		},
	};
});

export const useThemeMode = () => useThemeStore((state) => state.mode);
export const useResolvedTheme = () => useThemeStore((state) => state.resolved);
export const useIsLightTheme = () =>
	useThemeStore((state) => isLightTheme(state.resolved));
export const usePreviewTheme = () => useThemeStore((state) => state.preview);
export const useSetThemeMode = () => useThemeStore((state) => state.setMode);
